import { type ReactElement, useEffect, useRef } from "react";

import { Button, type ButtonProps } from "@/components/ui/button";
import { Kbd, KbdGroup } from "@/components/ui/kbd";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

type ShortcutButtonProps = ButtonProps & {
  /** Off when another action on the page could claim ⌘↵: the keys are then not shown either. */
  shortcut?: boolean;
};

/**
 * The page's main action, also triggered by ⌘↵ (Ctrl+↵ outside macOS), the keys shown inside the button as on
 * Resend. Hidden on small screens and touch devices, where there is no keyboard to press them.
 */
export function ShortcutButton({
  shortcut = true,
  onClick,
  disabled,
  loading,
  children,
  ...props
}: ShortcutButtonProps): ReactElement {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const active = shortcut && !disabled && !loading;

  useEffect(() => {
    if (!active) {
      return;
    }

    function onKeyDown(event: KeyboardEvent) {
      if (
        event.key !== "Enter" ||
        !(event.metaKey || event.ctrlKey) ||
        event.repeat ||
        event.defaultPrevented
      ) {
        return;
      }
      event.preventDefault();
      // A real click: same handler, same focus and pressed states as with the mouse.
      buttonRef.current?.click();
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [active]);

  // Not Button's own `loading`, which hides the label: the label stays, the spinner takes the keys' place.
  return (
    <Button
      ref={buttonRef}
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      aria-keyshortcuts={active ? "Meta+Enter Control+Enter" : undefined}
      {...props}
    >
      {children}
      {loading ? (
        <Spinner className="size-4 text-primary-foreground" />
      ) : shortcut ? (
        // Read as `aria-keyshortcuts`, not as part of the label.
        <KbdGroup
          aria-hidden
          className={cn("hidden pointer-coarse:hidden md:inline-flex", !active && "opacity-60")}
        >
          <Kbd className="bg-white/20 text-primary-foreground/90">⌘</Kbd>
          <Kbd className="bg-white/20 text-primary-foreground/90">↵</Kbd>
        </KbdGroup>
      ) : null}
    </Button>
  );
}
